<template>
  <div
    ref="container"
    class="container-fluid studio-container h-full"
    :class="getContainerClassList"
  >
    <transition name="fade-out">
      <tml-loader
        v-if="showLoading || isLoadingProduct"
        :loading="showLoading || isLoadingProduct"
        :text="isLoadingProduct ? 'Loading your product' : undefined"
        absolute
        fill
        class="full-page-loader"
      />
    </transition>
    <div class="row h-full">
      <div class="px-0 col-12 col-lg-11 studio-body-container h-full">
        <div class="studio-wrapper relative" @click="onStageClick">
          <div v-if="showGetStarted" class="get-started-container">
            <a
              v-if="!editMode"
              :style="getStartedStyle"
              class="get-started"
              href="#"
              @click.prevent.stop="getStartedClick"
            >
              <font-awesome-icon :icon="faPlusCircle" />
            </a>
          </div>
          <studio-canvas
            ref="canvas"
            class="pt-8 md:pt-0"
            :custom-options="{
              object: {
                showDimensions: true,
                retainPhysicalDimensions: designScalingMethod === 'real',
              },
              background: {
                imageEnabled: true,
              },
              foreground: {
                imageEnabled: true,
              },
              image: {
                onAfterInitialise: onImageElementAddedToStage,
                mipMapsEnabled: false,
              },
            }"
            @canvas-initialised="canvasInitialised"
            @after-canvas-initialised="afterCanvasInitialised"
            @canvas-destroyed="canvasDestroyed"
            @canvas-click="onCanvasClick"
          />
          <studio-toolbar
            :show-tools="hasProduct"
            :zoom="zoom"
            :active-el="activeEl"
            :object-count="objectCount"
            :show-col-palette="showColPalette"
            :help-popup-name="config.helpPopupName"
            show-help-tooltip
            @toggle-col-palette="showColPalette = !showColPalette"
            @filter="filterActiveElement"
            @zoom="applyZoom"
            @recolour="applyColourFilter"
            @help-dismissed="helpDismissed = true"
          />
          <tml-new-segmented-control
            v-if="activeProductObject && activeProductObject.magicModels3d"
            v-model="viewMode"
            name="activeViewModeSegmented"
            :options="viewModeOptions"
            class="view-mode-toggle"
          />
          <tml-spinner v-if="editMode && !hasProduct" absolute />
        </div>
        <studio-popup-slide
          v-show="hasCanvas"
          :force-products="shouldForceProducts"
        />
      </div>
      <div
        ref="navbarContainer"
        class="col-12 col-lg-1 studio-navigation-container"
      >
        <studio-navigation
          v-if="activeProductObject && hasCanvas"
          ref="navbar"
          :show-pip-on-save="showPipOnSave"
          product
          design
          text
          :qr="config.output === 'qr'"
          :save="config.output === 'save'"
          :buy="config.output === 'buy' || config.output === 'bulk-buy'"
          :save-template="config.output === 'template'"
          :help-dismissed="helpDismissed"
          @tab-selected="tabSelected"
        />
      </div>
    </div>

    <studio-help-popup v-if="config.helpPopupName" :is-approved="isApproved" />

    <studio-dev-tools v-if="devMode" :active-el="activeEl" />

    <tml-pseudo-keyboard v-if="isMobile()" :show="showColPalette">
      <template #content>
        <tml-tabbed-color-palette
          :default-color="activeEl?.color"
          :colours="['Black', 'Red', 'Yellow', 'Green', 'Blue', 'Purple']"
          circle-size="40px"
          @colour-selected="applyColourFilter"
        />
      </template>
    </tml-pseudo-keyboard>
    <tml-image-filter-popup
      v-if="filterableImage"
      start-open
      popup-name="image-filter-popup-studio"
      :image="filterableImage"
      @enter="onFilterOpen"
      @on-save="onFilterSave"
      @closed="onFilterPopupClosed"
    />
  </div>

  <product-mockup-3-d
    :model-data="
      activeProductObject ? activeProductObject.magicModels3d : undefined
    "
    :active-color-name="getVuexProperty('colour')"
    :design-file-url="designFilePreview"
    :config="config"
    @enter="on3dPreviewEnter"
    @leave="on3dPreviewBeforeLeave"
  >
    <template #footer>
      <tml-button
        text="Back to design mode"
        fill
        @click="on3dPreviewPurchase"
      />
    </template>
  </product-mockup-3-d>
</template>

<script>
import {StoreMixin} from '@teemill/components/mixins';
import {
  SierraCanvas,
  Vector,
  Logger,
  TmlStorage,
} from '@teemill/common/classes';
import {formatUrl, isBase64, isDeviceIos} from '@teemill/common/helpers';
import {events} from '@teemill/common/services';
import {Manufacturer} from '@teemill/common/models';

import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import StudioCanvas from './core/StudioCanvas.vue';
import StudioNavigation from './core/StudioNavigation.vue';
import StudioPopupSlide from './core/StudioPopupSlide.vue';
import StudioToolbar from './core/StudioToolbar.vue';
import StudioHelpPopup from './core/StudioHelpPopup.vue';

import globalMixins from './_global_mixins';
import studioMixins from './_studio_mixins';

import {TmlImageFilterPopup} from '@teemill/modules/images';

import {TmlPseudoKeyboard, TmlTabbedColorPalette} from '@teemill/components';
import {clamp} from 'lodash';
import {snackbar} from '@teemill/common/services';
import {overlays} from '@teemill/modules/overlays';

import ProductMockup3D from './elements/ProductMockup3D.vue';
import dayjs from 'dayjs';

const logger = new Logger('#2196F3', 'Studio');

export default {
  name: 'Studio',

  components: {
    StudioCanvas,
    StudioNavigation,
    StudioPopupSlide,
    StudioToolbar,
    StudioDevTools: () =>
      import(
        /* webpackChunkName: "StudioInternalComponents" */ './core/StudioDevTools.vue'
      ),
    StudioHelpPopup,
    TmlPseudoKeyboard,
    TmlImageFilterPopup,
    TmlTabbedColorPalette,
    ProductMockup3D,
  },

  mixins: [StoreMixin, studioMixins, globalMixins],

  inject: [
    'getProductUrlName', // comes from the page element, determines a product to edit
    'theme',
  ],

  provide() {
    return {
      logger: this.logger,
      getDesignsLoading: () => this.designsLoading,
      onElementSelected: this.onElementSelected,
      onElementDeselected: this.onElementDeselected,
      onElementActivated: this.onElementActivated,
      onElementAdded: this.onElementAdded,
      onElementDeleted: this.onElementDeleted,
      onProductSelected: this.onProductSelected,
      showGetStarted: () => this.showGetStarted,
    };
  },

  props: {
    isApproved: Boolean,
    default: false,
  },

  data() {
    return {
      containerClass: 'container-fluid',
      logger,
      zoom: 1,
      activeEl: false,
      // edit product data
      productUrlName: false,
      ready: false,

      showColPalette: false,
      hasProduct: false,
      hasShownBackprintWarning: false,
      validLandingStages: ['product-list', 'design-list', 'text-editor'],

      filterTarget: null,

      hue: 0,

      faPlusCircle,

      loggerOptions: {
        subheading: 'Studio.vue',
        subheadingColor: '#aa55ee',
      },

      isLoadingProduct: false,
      designsLoading: 0,

      showPipOnSave: false,

      objectCount: 0,

      helpDismissed: false,

      hasUnsavedDesignChanges: false,

      hasLoadedDefaultDesigns: false,

      getStartedPosition: null,
      getStartedPositionAnimationFrame: null,
      getStartedPositionTrackingActive: false,

      designFilePreview: null,

      viewMode: {text: 'Design', value: 'design'},
      viewModeOptions: [
        {text: 'Design', value: 'design'},
        {text: 'Preview', value: 'preview'},
      ],
      shouldForceProducts: false,
      showLoading: true,
    };
  },

  computed: {
    activeProductObject() {
      return this.getVuexProperty('activeProductObject');
    },

    hasCanvas() {
      return !!this.getCanvas();
    },

    duplicateMode() {
      return this.designUrl || this.backDesignUrl;
    },

    hasUnsavedChanges() {
      if (this.editMode || this.duplicateMode) {
        return this.hasUnsavedDesignChanges;
      }

      return this.hasUnsavedDesignChanges && !!this.objectCount;
    },

    getStartedStyle() {
      if (this.getStartedPosition) {
        return this.getStartedPosition;
      }

      return {};
    },
  },
  watch: {
    showGetStarted(val) {
      if (val) {
        this.getCanvas().params.bounds.force = true;
        this.getCanvas().render();
        this.$nextTick(() => {
          this.updateGetStartedPosition();
          this.startGetStartedPositionTracking();
        });
      } else {
        this.stopGetStartedPositionTracking();
        this.getCanvas().params.bounds.force = false;
        this.getCanvas().render();
      }
    },

    hue() {
      this.applyColourFilter({
        h: this.hue,
        s: 1,
        v: 1,
      });
    },

    designsLoading(value) {
      if (!value) {
        this.isLoadingProduct = false;
      }
    },

    hasUnsavedChanges() {
      this.$emit('editor-updated', {hasUnsavedChanges: this.hasUnsavedChanges});
    },

    viewMode(value) {
      if (value.value === 'preview') {
        overlays.open('3d-preview');
      } else {
        overlays.close('3d-preview');
      }
    },
  },

  created() {
    events.on('studio.product-saved', this.onProductSaved);
  },

  beforeUnmount() {
    events.off('studio.product-saved');
    events.off('studio.pickedProduct');
    window.removeEventListener('resize', this.resizeHandlers);
    window.removeEventListener('unload', this.onBeforeUnload);
    this.stopGetStartedPositionTracking();
  },

  mounted() {
    events.on('studio.pickedProduct', () => {
      this.whenBoundsLoaded('front', () => {
        const canvas = this.getCanvas();
        this.showLoading = false;
        this.updateGetStartedPosition(canvas);
      });
    });
  },

  methods: {
    formatUrl,
    isDeviceIos,

    startGetStartedPositionTracking() {
      if (this.getStartedPositionTrackingActive) {
        return;
      }

      this.getStartedPositionTrackingActive = true;

      const loop = () => {
        if (!this.getStartedPositionTrackingActive) {
          return;
        }

        this.updateGetStartedPosition();
        this.getStartedPositionAnimationFrame =
          window.requestAnimationFrame(loop);
      };

      loop();
    },

    stopGetStartedPositionTracking() {
      this.getStartedPositionTrackingActive = false;

      if (this.getStartedPositionAnimationFrame !== null) {
        window.cancelAnimationFrame(this.getStartedPositionAnimationFrame);
      }

      this.getStartedPositionAnimationFrame = null;
    },

    updateGetStartedPosition(canvas = this.getCanvas()) {
      const container = this.$refs.container;
      const wrapper = container?.querySelector('.studio-wrapper');
      const boundingBox = canvas?.boundingBox;
      const canvasScale = canvas?.params?.scale ?? 1;
      const canvasCenterPoint = canvas?.canvasCenterPoint;
      const canvasElementWidth = canvas?.canvasElement?.width;
      const canvasElementHeight = canvas?.canvasElement?.height;

      if (
        !wrapper ||
        !canvas?.canvasElement ||
        !canvasElementWidth ||
        !canvasElementHeight ||
        !boundingBox ||
        !canvasCenterPoint
      ) {
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const canvasRect = canvas.canvasElement.getBoundingClientRect();

      if (!canvasRect.width || !canvasRect.height) {
        return;
      }

      const boundsCenterX =
        canvasCenterPoint.x +
        (boundingBox.position.x + boundingBox.width / 2) * canvasScale;
      const boundsCenterY =
        canvasCenterPoint.y +
        (boundingBox.position.y + boundingBox.height / 2) * canvasScale;

      const canvasToClientScaleX = canvasRect.width / canvasElementWidth;
      const canvasToClientScaleY = canvasRect.height / canvasElementHeight;

      const left =
        canvasRect.left -
        wrapperRect.left +
        boundsCenterX * canvasToClientScaleX;
      const top =
        canvasRect.top - wrapperRect.top + boundsCenterY * canvasToClientScaleY;

      if (!Number.isFinite(left) || !Number.isFinite(top)) {
        return;
      }

      this.getStartedPosition = {
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
        transform: 'translate(-50%, -50%)',
      };
    },

    /**
     * Event handler for when a user clicks the 'get started' prompt
     * Opens the designs tab
     *
     * @return {void}
     */
    getStartedClick() {
      this.$refs.navbar.goToTab('design-list');
    },

    afterCanvasInitialised(canvas) {
      // TODO: make this dynamic
      this.setVuexProperty('canvasScale', 1200 / 2558);

      canvas.eventBus.once('after-product-selected', () => {
        this.handleUnsavedDesign();
      });

      canvas.eventBus.on('complete', () => {
        TmlStorage.local.remove('studio-canvas-content');
        window.removeEventListener('unload', this.onBeforeUnload);
      });

      window.addEventListener('unload', this.onBeforeUnload);

      this.whenBoundsLoaded('front', () => {
        if (!canvas.canvasElement) {
          // give up if the canvas has been destroyed between this listener being added and executed
          return;
        }
        this.showLoading = false;
        this.updateGetStartedPosition(canvas);
        if (this.showGetStarted) {
          this.startGetStartedPositionTracking();
        }
      });

      this.addResizeListener(() => {
        this.updateGetStartedPosition(canvas);
      });

      if (this.registeredVuexModule) {
        this.initialise();
      }

      this.$emit('after-canvas-initialised', canvas);
    },

    handleUnsavedDesign() {
      const template = TmlStorage.local.get('studio-canvas-content');

      if (!template?.createdAt) {
        return;
      }

      const isExpired = dayjs(template.createdAt).isBefore(
        dayjs().subtract(1, 'day')
      );

      if (!isExpired) {
        snackbar(
          'We detected a previous unsaved design',
          false,
          5000,
          'Restore',
          () => this.getCanvas().fromTemplate(template)
        );
      }
    },

    onBeforeUnload() {
      const canvas = this.getCanvas();

      if (canvas.getObjects().length) {
        const template = canvas.stageToTemplate();
        try {
          TmlStorage.local.set('studio-canvas-content', {
            ...template,
            createdAt: Date.now(),
          });
        } catch {
          // in the event that the browser has no local storage space left, do nothing
          return;
        }
      }
    },

    /**
     * Event triggered when any element is selected in the canvas
     *
     * @return {void}
     */
    onElementSelected(el) {
      this.hasUnsavedDesignChanges = true;

      this.activeEl = el;
    },

    /**
     * Event triggered when any element is deselected in the canvas
     *
     * @return {void}
     */
    onElementDeselected(element) {
      if (!element) {
        return;
      }

      if (element.id === this.activeEl?.id) {
        this.activeEl = false;
        this.showColPalette = false;
      }
    },

    onElementActivated(el) {
      if (el.className === 'Text' && !this.isMobile()) {
        this.setVuexProperty('activeTab', 'text-editor');
        this.setVuexProperty('activeDesignTab', 'text');
      }
    },

    onElementAdded(el) {
      this.objectCount = this.getCanvas().getObjects().length;

      this.logger.log(
        'element added',
        this.logger.flags.INFO,
        [
          ['type:', el.className],
          ['layer:', el.layer],
          ['full object:', el],
        ],
        this.loggerOptions
      );
      if (!this.hasShownBackprintWarning && el.layer === 'back') {
        if (el.source === 'editData') {
          /**
           * Do not show the warning for elements added during edit product
           * initialisation
           */
          this.hasShownBackprintWarning = true;
          return;
        }
        this.$dialogs.showSnackBar({
          message: 'Products with backprints cost more',
          timeout: 5000,
          actionText: 'Undo',
          action: () => {
            el.delete();

            /**
             * If they listen to the warning the first time, make sure they see
             * the same warning next time
             */
            this.hasShownBackprintWarning = false;
          },
        });
        /**
         * Only show the warning the first time
         */
        this.hasShownBackprintWarning = true;
      }

      if (this.config.showFirstTimeTooltips === 'dashboard') {
        this.openTooltipOnceIfDoesNotHaveBadge('next-step-save', 'designer');
      }

      this.showPipOnSave = true;
    },

    onElementDeleted() {
      if (this.getCanvas()) {
        this.objectCount = this.getCanvas().getObjects().length;
      }
    },

    /**
     * Triggered by ProductList.vue when a new product is selected
     *
     * @param {String} fromSku The previous product's SKU
     */
    onProductSelected(fromSku /* , toSku */) {
      if (fromSku && this.config.showFirstTimeTooltips === 'dashboard') {
        this.openTooltipOnceIfDoesNotHaveBadge('next-step-design', 'designer');
      }

      if (!this.editMode && !fromSku) {
        if (this.showGetStarted !== false) {
          this.showGetStarted = true;
          const defaultOptions = this.getVuexProperty('defaultOptions', {});

          if (defaultOptions?.designFile) {
            this.createImageElement(defaultOptions.designFile);
            this.showGetStarted = false;
          }

          if (defaultOptions?.backDesignFile) {
            this.createImageElement(defaultOptions.backDesignFile, {
              layer: 'back',
            });
            this.showGetStarted = false;
          }

          const stage = this.$route.query.stage;

          if (
            stage &&
            this.validLandingStages.includes(this.$route.query.stage)
          ) {
            this.showGetStarted = false;

            this.setVuexProperty('activeTab', stage);
          }

          if (this.$route.query.name) {
            this.setVuexProperty('productNamePrefill', this.$route.query.name);
          }
        }

        this.hasProduct = true;
      } else if (this.editMode) {
        if (!this.hasProduct) {
          this.setInitialCanvasState();
        }
        this.hasProduct = true;
      }

      // the first time a store owner selects a product, close the tab to prompt moving to next step
      if (this.config.showFirstTimeTooltips === 'dashboard') {
        if (
          !Manufacturer.hasBadge('designer') &&
          !this.getVuexProperty('shownTooltip:next-step-design')
        ) {
          this.setVuexProperty('activeTab', null);
        }
      }

      if (!this.hasLoadedDefaultDesigns) {
        this.loadDefaultDesigns();
      }

      this.getCanvas().eventBus.emit('after-product-selected');
    },

    loadDefaultDesigns() {
      if (this.designUrl) {
        this.createImageElementFromUrl(this.designUrl);
      }

      if (this.backDesignUrl) {
        this.createImageElementFromUrl(this.backDesignUrl, {
          layer: 'back',
        });
      }

      this.hasLoadedDefaultDesigns = true;
    },

    setInitialCanvasState() {
      const canvasState = this.getCanvas().stageToTemplate();

      if (canvasState) {
        this.setVuexProperty('initialCanvasState', JSON.stringify(canvasState));
      }
    },

    /**
     * When using Studio in edit mode, you must first request data about the
     * product
     *
     * @return {void}
     */
    enterEditMode() {
      this.editMode = true;
      this.isLoadingProduct = true;
      this.axios
        .post(formatUrl('/omnis/v3/dashboard/studio/editProduct/'), {
          productUrlName: this.productUrlName,
        })
        .then(response => {
          if (response.data.id) {
            this.setVuexProperty(
              'createProductAjaxURL',
              '/omnis/v3/dashboard/studio/updateTeemillProduct/'
            );

            const data = response.data;
            if (typeof data.price === 'string') {
              data.price = parseFloat(data.price);
            }

            this.editProductData = data;

            events.once('studio.pickedProduct', () => {
              this.loadProductFromEditProductData(this.editProductData);
            });

            events.emit('studio.forceProduct');

            this.setInitialCanvasState();

            if (this.designsLoading === 0) {
              this.isLoadingProduct = false;
            }
          } else {
            this.$router.replace('/404');
          }
        })
        .catch(({response}) => {
          if (response && response.status === 401) {
            this.$router.push('/login/');
          } else {
            this.$dialogs.showSnackBar({
              message: 'Sorry, something went wrong',
              type: 'error',
            });
          }
        });
    },

    duplicateProduct(urlName) {
      this.axios
        .post(formatUrl('/omnis/v3/dashboard/studio/editProduct/'), {
          productUrlName: urlName,
        })
        .then(response => {
          if (response.data.id) {
            const colours = Array.isArray(response.data.colours)
              ? response.data.colours
              : response.data.colours.split(', ');

            this.setVuexProperty('defaultOptions', {
              sku: response.data.sku,
              colour: colours[0] || 'White',
            });

            this.shouldForceProducts = true;

            events.once('studio.pickedProduct', () => {
              this.loadProductFromEditProductData(response.data);
            });

            events.emit('studio.forceProduct', response.data);

            this.setInitialCanvasState();

            if (this.designsLoading === 0) {
              this.isLoadingProduct = false;
            }
          } else {
            this.$router.replace('/404');
          }
        })
        .catch(({response}) => {
          if (response && response.status === 401) {
            this.$router.push('/login/');
          } else {
            this.$dialogs.showSnackBar({
              message: 'Sorry, something went wrong',
              type: 'error',
            });
          }
        });
    },

    loadProductFromEditProductData(data) {
      const template = data.personalizationTemplate || data.template;

      if (template) {
        this.whenBoundsLoaded('front', () => {
          this.getCanvas().fromTemplate(template);
          this.setInitialCanvasState();
        });
      } else {
        if (data.design_file_url) {
          this.designsLoading += 1;
          const img = new Image();

          img.onload = () => {
            if (!this.getCanvas()) {
              return;
            }

            this.isolateActivePixels(img, 'front', true);
            this.setInitialCanvasState();
            this.showGetStarted = false;
            this.designsLoading -= 1;
          };

          img.crossOrigin = 'Anonymous';

          this.whenBoundsLoaded('front', () => {
            img.src = formatUrl(data.design_file_url);
          });
        }

        if (data.back_design_file_url) {
          this.designsLoading += 1;
          const img = new Image();

          img.onload = () => {
            if (!this.getCanvas()) {
              return;
            }

            this.isolateActivePixels(img, 'back', true);
            this.setInitialCanvasState();
            this.showGetStarted = false;
            this.designsLoading -= 1;
          };

          img.crossOrigin = 'Anonymous';

          this.whenBoundsLoaded('back', () => {
            img.src = formatUrl(data.back_design_file_url);
          });
        }
      }
    },

    whenBoundsLoaded(layerName, callback) {
      if (!this.getCanvas()) {
        return;
      }

      const bounds = this.getCanvas().getLayerByName(layerName).bounds;

      if (bounds?.source === 'product') {
        // if the layer is ready, trigger the callback instantly
        callback();
      } else {
        // wait for the layer to load
        events.once(`studio.${layerName}BoundsSet`, () => {
          const bounds = this.getCanvas()?.getLayerByName(layerName).bounds;
          if (bounds?.source === 'product') {
            callback();
          } else {
            this.whenBoundsLoaded(layerName, callback);
          }
        });
      }
    },

    /**
     * Take an image and crop away any transparent padding
     *
     * @param {Image} img The image to crop
     * @return {HTMLCanvasElement} A cropped version of the image
     */
    isolateActivePixels(img, layer, fromEditProductData) {
      const cnv = new SierraCanvas();
      cnv.resizeToImage(img).drawImage(img, 0, 0);

      const padding = 20;

      let newImagePosition = new Vector(0, 0);

      // temporarily disable this method on Safari while investigating what has broken this
      if (window.safari === undefined) {
        newImagePosition = cnv.trim(0.25, padding);
      }

      cnv.setImageSmoothing(false);

      const activeLayer = this.getCanvas().getLayerByName(layer);
      const bounds = activeLayer.bounds;

      const boundsAspectRatio = bounds.width / bounds.height;
      const imgAspectRatio = img.width / img.height;

      const ratio =
        boundsAspectRatio > imgAspectRatio
          ? bounds.height / img.height
          : bounds.width / img.width;

      this.createImageElement(
        cnv.canvas,
        {
          position: new Vector(
            clamp((newImagePosition.x - padding) * ratio, 0, bounds.width),
            clamp((newImagePosition.y - padding) * ratio, 0, bounds.height)
          ),
          width: cnv.width * ratio,
          height: cnv.height * ratio,
          layer,
          source: fromEditProductData ? 'editData' : false,
        },
        true
      );
      return cnv.canvas;
    },

    /**
     * Event handler for when the stage is clicked. This closes any active tabs
     *
     * @return {void}
     */
    onStageClick() {
      if (!this.getCanvas().activeEl) {
        this.setVuexProperty('activeTab', null);
      }
    },

    /**
     * Performs some required initialisation commands to prepare the page for
     * Studio
     *
     * @return {void}
     */
    initialise() {
      this.productUrlName = this.getProductUrlName();
      if (this.productUrlName) {
        this.enterEditMode();
      }

      if (this.$route.query.duplicate) {
        this.duplicateProduct(this.$route.query.duplicate);
      } else {
        this.shouldForceProducts = true;
      }
      if (this.$route.query.template) {
        this.loadTemplate(this.$route.query.template);
      }

      window.addEventListener('resize', this.resizeHandlers);
      this.resizeHandlers();
    },

    createImageElementFromUrl(url, options) {
      const img = new Image();

      img.crossOrigin = 'anonymous';

      img.onload = () => {
        this.createImageElement(img, options);
      };

      img.src = url;
    },

    onFilterOpen() {
      if (this.activeEl && this.activeEl.className === 'Image') {
        this.filterTarget = this.activeEl;
      }
    },

    onFilterSave(img) {
      if (this.filterTarget) {
        this.filterTarget.changeImage(img, true);
      }
      this.getCanvas().render();
    },

    onCanvasClick() {
      if (
        this.activeEl?.className === 'Text' &&
        this.getVuexProperty('activeTab') === 'text-editor'
      ) {
        return;
      }

      this.setVuexProperty('activeTab', null);
    },

    onProductSaved() {
      this.$emit('saved');
      this.hasUnsavedDesignChanges = false;
    },

    onImageElementAddedToStage(object) {
      if (!this.config.uploadDesignsToCloud) {
        return;
      }

      const src = object.img_src || object.sourceUrl;

      // we only need to upload images that have been uploaded from the client
      if (!isBase64(src)) {
        return;
      }

      this.uploadImageElementSource(object);
    },

    uploadImageElementSource(object) {
      const maxSize = isDeviceIos() ? 2500 : 5000;

      const src = object.img_src || object.sourceUrl;
      const type = src.startsWith('data:image/jpeg')
        ? 'image/jpeg'
        : 'image/webp';

      object
        .toBlob(type, 0.98, {
          maxWidth: maxSize,
          maxHeight: maxSize,
        })
        .then(blob => {
          const data = new FormData();
          data.append(
            'file',
            blob,
            `uploaded-image.${blob.type.split('/')[1]}`
          );

          return this.axios
            .post(formatUrl('/omnis/v3/files/images'), data, {
              headers: {
                'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
              },
            })
            .success(response => {
              object.sourceUrl = response.url + '?q=0';
              object.referenceId = response.id;

              this.axios.post(
                formatUrl(
                  `/omnis/v3/dashboard/studio/associate-design/${response.id}`
                )
              );
            });
        });
    },

    on3dPreviewEnter() {
      this.designFilePreview = this.getCanvas().export('image/webp', 0.92, 4);
    },

    on3dPreviewBeforeLeave() {
      this.designFilePreview = null;

      this.viewMode = {
        value: 'design',
        text: 'Design',
      };
    },

    on3dPreviewPurchase() {
      overlays.close('3d-preview');
    },
  },
};
</script>

<style lang="scss" scoped>
.studio-container {
  overflow: hidden;
  padding-right: 0.5em;
  padding-left: 0.5em;
  background: var(--tml-page-background-color);

  * {
    -webkit-tap-highlight-color: transparent;
  }
  .get-started-container {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 10;
  }

  .get-started {
    position: absolute;
    top: 50%;
    left: 50%;
    right: auto;
    text-align: center;
    font-size: 20px;
    transform: translate(-50%, -50%);
    z-index: 10;
    color: $text-color;
    pointer-events: auto;
    transition: color 0s;

    &:active {
      color: $text-color;
    }
    &:hover {
      color: $light-text-color;
    }

    svg {
      font-size: 40px;
    }
  }
  .col-palette {
    text-align: center;
    margin-left: 0;
    margin-top: 10px;
  }
}

.studio-navigation-container {
  position: relative;
  background: var(--tml-page-background-color);
  border-color: var(--tml-page-background-color);
}

.view-mode-toggle {
  position: absolute;
  left: 50%;
  transform: translateX(-50%) translateY(50%);
  z-index: 100;
  min-width: 200px;
}

@media (min-width: 992px) {
  .studio-navigation-container {
    border-left: 1px solid $off-white-color;
  }
}

@media (max-height: 400px) {
  .studio-body-container {
    min-height: 400px;
  }
}

.right-tip {
  position: absolute;
  top: 0;
  right: 0;
}

@media (max-width: 991px) {
  .slide-open {
    .studio-controls {
      display: none;
    }
  }

  .view-mode-toggle {
    bottom: 0;
    transform: translateX(-50%) translateY(-150%);
  }
}

@media (min-width: 992px) {
  .studio-wrapper {
    width: 100%;
    position: relative;
    transition: width 0.25s ease;
  }

  .slide-open .studio-wrapper {
    width: 50%;
  }

  .slide-closing .studio-wrapper {
    width: 100%;
  }

  .studio-navigation-container {
    padding: 1.25em 0;
  }

  .view-mode-toggle {
    top: 0;
  }
}
</style>

<style lang="scss">
textarea.omnicanvas-textEditor {
  position: absolute;
  font-size: 16px;
  height: 1px;
  width: 1px;
  z-index: -99;
  top: 0;
  opacity: 0;
}

.studio-container {
  .omnicanvas-bg-img-container {
    position: absolute;
    left: 0;
    right: 0;
    text-align: center;
    /* width: 100%; */

    .omnicanvas-bg-img {
      /* position: relative; */
      max-width: 100%;
      max-height: calc(100vh - 60px);
      display: inline-block;
    }
  }
}

.full-page-loader {
  position: absolute;
  z-index: 100;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  background: white;
}

.fade-out-leave-active {
  transition: all 0.3s ease-in;
}
.fade-out-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
